import React from 'react'
import PropTypes from 'prop-types'
import styles from './Status.module.scss'

export const STATUSES = {
  approved:       { label: 'Approved',                token: '--status-approved' },
  cautionary:     { label: 'Cautionary approval',     token: '--status-cautionary' },
  'under-review': { label: 'Under review',            token: '--status-under-review' },
  new:            { label: 'New',                     token: '--status-new' },
  draft:          { label: 'Draft',                   token: '--status-draft' },
  cancelled:      { label: 'Cancelled',               token: '--status-cancelled' },
  pending:        { label: 'Pending',                 token: '--status-pending' },
  'more-info':    { label: 'Needs more information',  token: '--status-more-info' },
  declined:       { label: 'Declined',                token: '--status-declined' },
}

export const STATUS_KEYS = Object.keys(STATUSES)

export const toStatusKey = value => {
  if (!value) return null
  const raw = String(value).trim()
  if (STATUSES[raw]) return raw
  const lowered = raw.toLowerCase()
  return STATUS_KEYS.find(k => STATUSES[k].label.toLowerCase() === lowered) || null
}

const cx = (...p) => p.filter(Boolean).join(' ')

export const StatusIndicator = ({ status, label, className, ...rest }) => {
  const key = toStatusKey(status)
  if (!key) {
    if (process.env.NODE_ENV !== 'production') {
      throw new Error(
        `StatusIndicator: unknown status "${status}". Expected one of: ${STATUS_KEYS.join(', ')}`
      )
    }
    return null
  }

  return (
    <span className={cx(styles.status, className)} {...rest}>
      <span
        className={styles.swatch}
        style={{ '--_tone': `var(${STATUSES[key].token})` }}
        aria-hidden="true"
      />
      {label || STATUSES[key].label}
    </span>
  )
}

StatusIndicator.propTypes = {
  status: PropTypes.string.isRequired,

  label: PropTypes.node,
  className: PropTypes.string,
}

export const RISK_TIERS = {
  low:      { label: 'Low',      token: '--risk-low' },
  moderate: { label: 'Moderate', token: '--risk-moderate' },
  elevated: { label: 'Elevated', token: '--risk-elevated' },
  high:     { label: 'High',     token: '--risk-high' },
}

export const RISK_TIER_KEYS = Object.keys(RISK_TIERS)

export const RiskMeter = ({ tier, value, label, max = 100, className, ...rest }) => {
  const spec = RISK_TIERS[tier]
  if (!spec) {
    if (process.env.NODE_ENV !== 'production') {
      throw new Error(
        `RiskMeter: unknown tier "${tier}". Expected one of: ${RISK_TIER_KEYS.join(', ')}`
      )
    }
    return null
  }
  const text = label || spec.label
  const pct = Math.max(0, Math.min(100, (value / max) * 100))

  return (
    <div className={cx(styles.meter, className)} {...rest}>
      <span className={styles.meterLabel}>{text}</span>
      <div
        className={styles.track}
        role="progressbar"
        aria-label={text}
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
      >
        <div
          className={styles.fill}
          style={{ width: `${pct}%`, '--_tone': `var(${spec.token})` }}
        />
      </div>
      <span className={styles.value}>{value}</span>
    </div>
  )
}

RiskMeter.propTypes = {
  tier: PropTypes.oneOf(RISK_TIER_KEYS).isRequired,
  value: PropTypes.number.isRequired,
  label: PropTypes.node,
  max: PropTypes.number,
  className: PropTypes.string,
}

export default StatusIndicator
